import React, { useState, useMemo, useContext, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  Paper,
} from "@mui/material";
import {
  GoogleMap,
  LoadScript,
  Autocomplete as GMapAutocomplete,
  MarkerF
} from "@react-google-maps/api";
import { UserContext } from "../../context/UserContext";
import Swal from "sweetalert2";
import packet from "./../../../assets/img/maps/entrega-map.svg";
import departure from "./../../../assets/img/maps/shop_location_pin.svg";
import OrderService from "../../../services/OrderService";
import renameKeys from "../../../helper/renameKeys";
import InputMask from "react-input-mask";
import { MaskedCurrencyInput } from "../../components/MaskedCurrencyInput";

const containerStyle = { width: "100%", height: "400px" };

// Wrapper para telefone
const PhoneMask = (props) => {
  const { inputRef, ...other } = props;
  return (
    <InputMask
      {...other}
      ref={inputRef}
      mask="(99) 99999-9999"
      placeholder="(00) 00000-0000"
    />
  );
};

export default function XpressRequest() {
  const { selectedStore } = useContext(UserContext);
  const [autocomplete, setAutocomplete] = useState(null);
  const orderService = useMemo(() => new OrderService(), []);
  const [tipoPedido, setTipoPedido] = useState("PATTERN");
  const [aditionalFee, setAditionalFee] = useState(0);

  const [payload, setPayload] = useState({
    requester: {
      name: "",
      cellNumber: "",
      address: {
        cep: "",
        street: "",
        number: "",
        neighborhood: "",
        city: "",
        state: "",
        complement: "",
        location: { type: "Point", coordinates: [0, 0] },
      },
    },
    orderDetails: {
      payment: {
        returnFee: 0,
        price: 0,
        shipping: 0,
        type: "Pago",
        installments: null,
        change: 0,
      },
      orderType: "PATTERN",
      message: "",
      distance: 0,
    },
    store: renameKeys(selectedStore),
  });

  const lojaCoords = useMemo(
    () => ({
      lat: selectedStore.address.location.coordinates[0],
      lng: selectedStore.address.location.coordinates[1],
    }),
    [selectedStore]
  );

  const destinoCoords = useMemo(
    () => ({
      lat: payload.requester.address.location.coordinates[0],
      lng: payload.requester.address.location.coordinates[1],
    }),
    [payload]
  );

  const mapRef = useRef(null);

  const onLoad = (autoC) => setAutocomplete(autoC);
  const onMapLoad = (map) => (mapRef.current = map);

  const onPlaceChanged = () => {
    try {
      if (autocomplete !== null) {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const location = place.geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          setPayload((prev) => ({
            ...prev,
            requester: {
              ...prev.requester,
              address: {
                ...prev.requester.address,
                street: place.formatted_address || prev.requester.address.street,
                number:
                  place.address_components?.find((c) =>
                    c.types.includes("street_number")
                  )?.long_name || "",
                cep:
                  place.address_components?.find((c) =>
                    c.types.includes("postal_code")
                  )?.long_name || "",
                neighborhood:
                  place.address_components?.find((c) =>
                    c.types.includes("sublocality")
                  )?.long_name || "",
                city:
                  place.address_components?.find((c) =>
                    c.types.includes("administrative_area_level_2")
                  )?.long_name || "",
                state:
                  place.address_components?.find((c) =>
                    c.types.includes("administrative_area_level_1")
                  )?.short_name || "",
                location: {
                  type: "Point",
                  coordinates: [lat, lng],
                },
              },
            },
          }));

          // 👇 origem e destino dependem do tipo
          const origin =
            tipoPedido === "PATTERN" ? lojaCoords : { lat, lng };
          const destination =
            tipoPedido === "PATTERN" ? { lat, lng } : lojaCoords;

          const service = new window.google.maps.DirectionsService();
          service.route(
            {
              origin,
              destination,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === "OK" && result.routes.length > 0) {
                const distanceMeters = result.routes[0].legs[0].distance.value;
                setPayload((prev) => ({
                  ...prev,
                  orderDetails: {
                    ...prev.orderDetails,
                    distance: distanceMeters,
                  },
                }));

                // Ajusta zoom
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(origin);
                bounds.extend(destination);
                mapRef.current.fitBounds(bounds);
              } else {
                Swal.fire({
                  icon: "error",
                  title: "Erro ao calcular rota",
                  text: `Status: ${status}`,
                  color: "#000",
                  confirmButtonColor: "#555",
                });
              }
            }
          );
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Falha",
        text: "Erro inesperado ao definir o endereço.",
        color: "#000",
        confirmButtonColor: "#555",
      });
    }
  };

  const taxaEntrega = useMemo(() => {
    if (!payload.orderDetails.distance) return 0;
    const km = payload.orderDetails.distance / 1000;
    const fee = selectedStore.tariffs.fees.find(
      (f) => km >= f.minDistance && km <= f.maxDistance
    );
    const deliveryFee = fee ? fee.fee : selectedStore.tariffs.ratePerKm * km
    let aditionalFee = 0;
    if (tipoPedido === "REVERSE") {
      setAditionalFee(0);
      aditionalFee = deliveryFee * 0.5;
    } else if (tipoPedido === "SWITCH") {
      setAditionalFee(deliveryFee);
    }

    return deliveryFee + aditionalFee;
  }, [payload, selectedStore, tipoPedido]);

  const handleSubmit = async () => {
    try {
      let payloadFinal = { ...payload };

      if (tipoPedido === "REVERSE") {
        payloadFinal = {
          ...payload,
          store: {
            id: selectedStore._id,
            companyName: payload.requester.name,
            cellNumber: payload.requester.cellNumber,
            address: {
              cep: payload.requester.address.cep !== "" ?? selectedStore.address.cep,
              street: payload.requester.address.street,
              number: payload.requester.address.number !== "" ?? "s/N",
              neighborhood: payload.requester.address.neighborhood,
              complement: payload.requester.address.complement,
              city: payload.requester.address.city,
              state: payload.requester.address.state,
              location: payload.requester.address.location
            }
          },
          requester: {
            name: selectedStore.companyName,
            cellNumber: selectedStore.cellNumber,
            cpf: selectedStore.cnpj,
            address: {
              cep: selectedStore.address.cep,
              street: selectedStore.address.street,
              number: selectedStore.address.number,
              neighborhood: selectedStore.address.neighborhood,
              complement: selectedStore.address.complement,
              city: selectedStore.address.city,
              state: selectedStore.address.state,
              location: selectedStore.address.location,
              reference: ''
            }
          },
          orderDetails: {
            ...payload.orderDetails,
            orderType: tipoPedido
          }
        };
      } else {
        payloadFinal = {
          ...payload,
          orderDetails: {
            ...payload.orderDetails,
            orderType: tipoPedido
          }
        };
      }

      if (tipoPedido === "PATTERN") {
        payloadFinal.orderDetails.payment.returnFee = taxaEntrega * 0.3;
      }

      payloadFinal.orderDetails.payment.shipping = taxaEntrega;

      const response = await orderService.enviarOrder(payloadFinal);

      if (response) {
        Swal.fire({
          icon: "success",
          title: "Solicitação enviada!",
          color: "#000",
          confirmButtonColor: "#555",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro ao enviar",
          text: "Verifique os dados e tente novamente.",
          color: "#000",
          confirmButtonColor: "#555",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Falha de conexão",
        text: "Não foi possível se conectar ao servidor.",
        color: "#000",
        confirmButtonColor: "#555",
      });
    }
  };

  useEffect(() => {
    if (payload.orderDetails.payment.type !== "Pago") {
      setAditionalFee(taxaEntrega * 0.3);
    } else {
      setAditionalFee(0);
    }
  }, [payload, taxaEntrega]);


  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Solicitar Entrega
      </Typography>

      <TextField
        select
        label="Tipo de Pedido"
        fullWidth
        sx={{ mb: 2 }}
        value={tipoPedido}
        onChange={(e) => setTipoPedido(e.target.value)}
      >
        <MenuItem value="PATTERN">Padrão</MenuItem>
        <MenuItem value="REVERSE">Reversa</MenuItem>
        <MenuItem value="SWITCH">Troca</MenuItem>
      </TextField>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1">
            Loja: {selectedStore.companyName}
          </Typography>
          <Typography variant="body2">
            Endereço da Loja:{" "}
            {`${selectedStore.address.street}, ${selectedStore.address.number}, ${selectedStore.address.neighborhood}, ${selectedStore.address.city}, ${selectedStore.address.state}`}
          </Typography>

          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_MAPS_APP_KEY}
            libraries={["places"]}
          >
            <GMapAutocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
              <TextField
                label={
                  tipoPedido === "PATTERN"
                    ? "Endereço de Entrega"
                    : "Endereço de Coleta"
                }
                fullWidth
                sx={{ mt: 2, mb: 2 }}
              />
            </GMapAutocomplete>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={lojaCoords}
              zoom={14}
              onLoad={onMapLoad}
            >
              <MarkerF position={lojaCoords} icon={departure} label="Loja" />
              {payload.requester.address.location.coordinates[0] !== 0 && (
                <MarkerF
                  position={destinoCoords}
                  icon={packet}
                  label={tipoPedido === "PADRAO" ? "Destino" : "Coleta"}
                />
              )}
            </GoogleMap>
          </LoadScript>
          <Box mt={2}>
            <Typography variant="body1">
              R$ {taxaEntrega.toFixed(2)} — Taxa de entrega
            </Typography>
            <Typography variant="body1">
              R$ {aditionalFee.toFixed(2)} — Taxas adicionais
            </Typography>
          </Box>
        </Grid>

        {/* Dados e pagamento iguais */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: "#EEEEEE", color: "#000" }}>
            <Typography variant="subtitle1" gutterBottom>
              Dados do Destinatário
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Nome"
                  fullWidth
                  value={payload.requester.name}
                  onChange={(e) =>
                    setPayload((prev) => ({
                      ...prev,
                      requester: { ...prev.requester, name: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Telefone"
                  fullWidth
                  value={payload.requester.cellNumber}
                  onChange={(e) =>
                    setPayload((prev) => ({
                      ...prev,
                      requester: { ...prev.requester, cellNumber: e.target.value },
                    }))
                  }
                  InputProps={{
                    inputComponent: PhoneMask,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observações"
                  fullWidth
                  multiline
                  rows={3}
                  value={payload.orderDetails.message}
                  onChange={(e) =>
                    setPayload((prev) => ({
                      ...prev,
                      orderDetails: {
                        ...prev.orderDetails,
                        message: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Dados do Pagamento
            </Typography>
            <TextField
              select
              label="Forma de pagamento"
              fullWidth
              value={payload.orderDetails.payment.type}
              onChange={(e) =>
                setPayload((prev) => ({
                  ...prev,
                  orderDetails: {
                    ...prev.orderDetails,
                    payment: {
                      ...prev.orderDetails.payment,
                      type: e.target.value,
                      price: 0,
                      change: 0
                    },
                  },
                }))
              }
              sx={{ mb: 2 }}
            >
              <MenuItem value="Cartão de Crédito">Crédito</MenuItem>
              <MenuItem value="Cartão de Débito">Débito</MenuItem>
              <MenuItem value="Dinheiro">Dinheiro</MenuItem>
              <MenuItem value="Pago">Pago</MenuItem>
            </TextField>
            <MaskedCurrencyInput
              sx={{ mb: 2 }}
              fullWidth
              disabled={tipoPedido !== "PATTERN" || payload.orderDetails.payment.type === "Pago"}
              label="Valor do pedido"
              value={payload.orderDetails.payment.price}
              onChange={(value) =>
                setPayload((prev) => ({
                  ...prev,
                  orderDetails: {
                    ...prev.orderDetails,
                    payment: {
                      ...prev.orderDetails.payment,
                      price: parseFloat(value),
                    },
                  },
                }))
              }
            />
            <MaskedCurrencyInput
              label="Troco para"
              error={payload.orderDetails.payment.type === "Dinheiro" && payload.orderDetails.payment.change < payload.orderDetails.payment.price}
              helperText={payload.orderDetails.payment.type === "Dinheiro" && payload.orderDetails.payment.change < payload.orderDetails.payment.price ? "O valor a receber tem que ser maior ou igual ao valor do pedido" : ""}
              disabled={payload.orderDetails.payment.type !== "Dinheiro"}
              fullWidth
              value={payload.orderDetails.payment.change}
              onChange={(value) =>
                setPayload((prev) => ({
                  ...prev,
                  orderDetails: {
                    ...prev.orderDetails,
                    payment: {
                      ...prev.orderDetails.payment,
                      change: parseFloat(value),
                    },
                  },
                }))
              }
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#333",
                "&:hover": { bgcolor: "#555" },
                color: "#fff",
              }}
              onClick={handleSubmit}
            >
              Solicitar entrega
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
